/**
 * React Query hooks for WhatsApp templates (SDK WhatsAppTemplateService, Communications API api/whatsapp-templates).
 * scope "forms" uses the Forms routes (Forms admins without Communications rights; Forms messages only).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useWhatsAppTemplateService } from '@actschurch/ccms-sdk';
import type { SaveWhatsAppTemplateLinkRequest, WhatsAppTemplateLinkView } from '@actschurch/ccms-sdk';

export type WhatsAppTemplateScope = 'communications' | 'forms';

export const whatsAppTemplateKeys = {
  all: ['whatsapp-templates'] as const,
  library: (scope: WhatsAppTemplateScope, approvedOnly: boolean) => ['whatsapp-templates', 'library', scope, approvedOnly] as const,
  messages: (scope: WhatsAppTemplateScope, module?: string) => ['whatsapp-templates', 'messages', scope, module ?? 'all'] as const,
  ai: ['whatsapp-templates', 'ai'] as const,
};

/** Every synced template (communications) or the linkable ones (forms: approved only unless all). */
export function useWhatsAppTemplateLibrary(scope: WhatsAppTemplateScope = 'communications', approvedOnly = false, enabled = true) {
  const service = useWhatsAppTemplateService();
  return useQuery({
    queryKey: whatsAppTemplateKeys.library(scope, approvedOnly),
    queryFn: () => (scope === 'forms' ? service.getFormsLibrary(!approvedOnly) : service.getLibrary(approvedOnly)),
    enabled,
  });
}

/** The CCMS messages (communications: all, or one module) or the nine Forms messages (forms). */
export function useWhatsAppMessages(scope: WhatsAppTemplateScope = 'communications', module?: string, enabled = true) {
  const service = useWhatsAppTemplateService();
  return useQuery({
    queryKey: whatsAppTemplateKeys.messages(scope, module),
    queryFn: () => (scope === 'forms' ? service.getFormsMessages() : service.getMessages(module)),
    enabled,
  });
}

export function useWhatsAppTemplateAi(enabled = true) {
  const service = useWhatsAppTemplateService();
  return useQuery({ queryKey: whatsAppTemplateKeys.ai, queryFn: () => service.getAiAvailability(), enabled, staleTime: 60_000 });
}

export function useSyncWhatsAppTemplates() {
  const service = useWhatsAppTemplateService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => service.syncLibrary(),
    onSuccess: () => qc.invalidateQueries({ queryKey: whatsAppTemplateKeys.all }),
  });
}

export function useSaveWhatsAppTemplateLink(scope: WhatsAppTemplateScope) {
  const service = useWhatsAppTemplateService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, request }: { templateId: number; request: SaveWhatsAppTemplateLinkRequest }): Promise<WhatsAppTemplateLinkView> =>
      scope === 'forms' ? service.saveFormsLink(templateId, request) : service.saveLink(templateId, request),
    onSuccess: () => qc.invalidateQueries({ queryKey: whatsAppTemplateKeys.all }),
  });
}

export function useDeleteWhatsAppTemplateLink(scope: WhatsAppTemplateScope) {
  const service = useWhatsAppTemplateService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: number) => (scope === 'forms' ? service.deleteFormsLink(templateId) : service.deleteLink(templateId)),
    onSuccess: () => qc.invalidateQueries({ queryKey: whatsAppTemplateKeys.all }),
  });
}

/** The message of a CCMS error (CCMSError keeps the server's message and errors list). */
export function whatsAppErrorText(err: unknown, fallback: string): string {
  const e = err as { message?: string; errors?: unknown } | undefined;
  const list = Array.isArray(e?.errors) ? (e!.errors as unknown[]).map((x) => (typeof x === 'string' ? x : (x as { message?: string })?.message)).filter(Boolean) : [];
  const message = e?.message && e.message !== '[object Object]' ? e.message : fallback;
  return list.length > 0 ? `${message} ${list.join(' ')}` : message;
}
